import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle } from 'lucide-react'
import { api } from '../../lib/api'
import { Button, Modal, Tile, TileContent, TileDescription, TileHeader, TileTitle } from '../ui'

type PolicyRole = {
  name: string
  display_name: string
}

type PolicyPayload = {
  policy: Record<string, boolean>
  roles: PolicyRole[]
}

function PolicyRoleRow({
  role,
  checked,
  disabled,
  onToggle
}: {
  role: PolicyRole
  checked: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 dark:border-gray-700">
      <div>
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{role.display_name}</span>
        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({role.name})</span>
      </div>
      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          disabled={disabled}
        />
        Pflicht
      </label>
    </div>
  )
}

export default function MfaPolicyPanel() {
  const [policy, setPolicy] = useState<Record<string, boolean> | null>(null)
  const [roles, setRoles] = useState<PolicyRole[]>([])
  const [saving, setSaving] = useState(false)
  const [confirmAdminOff, setConfirmAdminOff] = useState(false)
  const [pendingPolicy, setPendingPolicy] = useState<Record<string, boolean> | null>(null)

  const applyPayload = (payload: PolicyPayload | null) => {
    if (!payload) return
    setPolicy(payload.policy || {})
    setRoles(payload.roles || [])
  }

  const loadPolicy = async () => {
    const response = await api.get('/auth/mfa/admin/policy')
    applyPayload(response.data?.data || null)
  }

  useEffect(() => {
    void loadPolicy()
  }, [])

  const savePolicy = async (nextPolicy: Record<string, boolean>) => {
    setSaving(true)
    try {
      const response = await api.put('/auth/mfa/admin/policy', nextPolicy)
      applyPayload(response.data?.data || { policy: nextPolicy, roles })
      toast.success('2FA-Pflicht aktualisiert')
    } catch (error: any) {
      toast.error(error.response?.data?.message || '2FA-Pflicht konnte nicht gespeichert werden')
    } finally {
      setSaving(false)
      setPendingPolicy(null)
    }
  }

  const handleToggle = async (roleName: string) => {
    if (!policy) return
    const next = { ...policy, [roleName]: !policy[roleName] }
    if (roleName === 'admin' && policy.admin && !next.admin) {
      setPendingPolicy(next)
      setConfirmAdminOff(true)
      return
    }
    await savePolicy(next)
  }

  if (!policy) {
    return (
      <Tile>
        <TileContent>
          <p className="text-sm text-gray-600 dark:text-gray-300">Lade 2FA-Pflichten...</p>
        </TileContent>
      </Tile>
    )
  }

  const sortedRoles = [...roles].sort((a, b) =>
    a.display_name.localeCompare(b.display_name, 'de')
  )

  return (
    <>
      <Tile>
        <TileHeader>
          <TileTitle as="h3">2FA-Pflicht pro Rolle</TileTitle>
          <TileDescription>
            Standardmäßig ist 2FA für alle Rollen deaktiviert. Aktivieren Sie die Pflicht gezielt pro Rolle.
            Hat ein Benutzer mehrere Rollen, gilt 2FA sobald mindestens eine davon die Pflicht hat.
          </TileDescription>
        </TileHeader>
        <TileContent className="space-y-3">
          {sortedRoles.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">Keine konfigurierbaren Rollen gefunden.</p>
          ) : (
            sortedRoles.map((role) => (
              <PolicyRoleRow
                key={role.name}
                role={role}
                checked={Boolean(policy[role.name])}
                disabled={saving}
                onToggle={() => { void handleToggle(role.name) }}
              />
            ))
          )}
        </TileContent>
      </Tile>

      <Modal
        isOpen={confirmAdminOff}
        onClose={() => {
          setConfirmAdminOff(false)
          setPendingPolicy(null)
        }}
        title="Admin-2FA wirklich deaktivieren?"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmAdminOff(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmAdminOff(false)
                if (pendingPolicy) {
                  void savePolicy(pendingPolicy)
                }
              }}
            >
              Ja, deaktivieren
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500" />
          <p>
            Wenn diese Option deaktiviert ist, können sich Admin-Konten ohne zweiten Faktor anmelden.
            Diese Aktion sollte nur als Notfall-Schalter genutzt werden.
          </p>
        </div>
      </Modal>
    </>
  )
}
